// services/supportService.ts
import api from "./api";
import Pusher from "pusher-js";

// ── Types ─────────────────────────────────────────────────────────────────

export interface SupportTicket {
  id: number;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  unread_count: number;
  last_message_at: string | null;
  created_at: string;
  latest_message?: string | null;
  user?: { id: number; username: string; email: string };
}

export interface SupportMessage {
  id: number;
  ticket_id: number;
  sender_id: number;
  sender_type: "user" | "admin";
  sender_name: string;
  sender_email?: string;
  message: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: "image" | "pdf" | "file" | null;
  is_read: boolean;
  created_at: string;
}

// ── Pusher singleton ──────────────────────────────────────────────────────

let pusherInstance: Pusher | null = null;

export const getPusherInstance = (): Pusher => {
  if (!pusherInstance) {
    // Strip /public/api, /api, or /public from the end to get the bare domain
    const baseUrl = (process.env.REACT_APP_API_URL ?? "")
      .replace(/\/(public\/)?api\/?$/, "")
      .replace(/\/public\/?$/, "");

    pusherInstance = new Pusher("87d3e8ab928411567a03", {
      cluster: "mt1",
      forceTLS: true,
      // Use a custom authorizer so the Bearer token is read FRESH on every
      // auth request — not captured once at instantiation (which bakes in
      // "Bearer null" if Pusher is created before the user has logged in).
      authorizer: (channel: { name: string }) => ({
        authorize: (
          socketId: string,
          callback: (error: any, authData: any) => void,
        ) => {
          const token = localStorage.getItem("token");
          // Use /api/broadcasting/auth — goes through the same public/api path as all other routes
          const authUrl = `${baseUrl}/broadcasting/auth`;
          console.log(
            `[Pusher Auth] POST ${authUrl} channel=${channel.name} token=${token ? "present" : "MISSING"}`,
          );
          fetch(authUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name,
            }),
          })
            .then((res) => {
              console.log(
                `[Pusher Auth] response status: ${res.status} for ${channel.name}`,
              );
              if (!res.ok) throw new Error(`Pusher auth failed: ${res.status}`);
              return res.json();
            })
            .then((data) => {
              console.log(`[Pusher Auth] ✅ auth OK for ${channel.name}`);
              callback(null, data);
            })
            .catch((err) => {
              console.error(
                `[Pusher Auth] ❌ auth FAILED for ${channel.name}:`,
                err,
              );
              callback(err, null);
            });
        },
      }),
    });
  }
  return pusherInstance;
};

export const refreshPusherAuth = () => {
  if (pusherInstance) {
    (pusherInstance.config as any).auth = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        Accept: "application/json",
      },
    };
  }
};

export const destroyPusher = () => {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
};

// ── User API ──────────────────────────────────────────────────────────────

const supportService = {
  getUserTickets: async (): Promise<SupportTicket[]> => {
    const res = await api.get("/support/tickets");
    return res.data.tickets;
  },

  createTicket: async (subject: string, message: string): Promise<any> => {
    const res = await api.post("/support/tickets", { subject, message });
    return res.data.ticket;
  },

  getTicketMessages: async (ticketId: number) => {
    const res = await api.get(`/support/tickets/${ticketId}/messages`);
    return res.data;
  },

  sendMessage: async (
    ticketId: number,
    message: string | null,
    attachment?: File | null,
  ): Promise<SupportMessage> => {
    const formData = new FormData();
    if (message) formData.append("message", message);
    if (attachment) formData.append("attachment", attachment);
    const res = await api.post(
      `/support/tickets/${ticketId}/messages`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return res.data.message;
  },

  sendTyping: async (ticketId: number, isTyping: boolean) => {
    await api
      .post(`/support/tickets/${ticketId}/typing`, { is_typing: isTyping })
      .catch(() => {});
  },

  adminSendTyping: async (ticketId: number, isTyping: boolean = true) => {
    await api
      .post(`/admin/support/tickets/${ticketId}/typing`, {
        is_typing: isTyping,
      })
      .catch(() => {});
  },

  closeTicket: async (ticketId: number) => {
    const res = await api.post(`/support/tickets/${ticketId}/close`);
    return res.data;
  },

  getUnreadCount: async (): Promise<number> => {
    const res = await api.get("/support/unread-count");
    return res.data.unread_count;
  },

  // ── Admin API ──────────────────────────────────────────────────────────

  adminGetAllTickets: async (filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters);
    const res = await api.get(`/admin/support/tickets?${params}`);
    return res.data;
  },

  adminGetTicketMessages: async (ticketId: number) => {
    const res = await api.get(`/admin/support/tickets/${ticketId}/messages`);
    return res.data;
  },

  adminSendMessage: async (
    ticketId: number,
    message: string | null,
    attachment?: File | null,
  ): Promise<SupportMessage> => {
    const formData = new FormData();
    if (message) formData.append("message", message);
    if (attachment) formData.append("attachment", attachment);
    const res = await api.post(
      `/admin/support/tickets/${ticketId}/messages`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data.message;
  },

  adminUpdateStatus: async (
    ticketId: number,
    status: string,
    priority?: string,
  ) => {
    const res = await api.put(`/admin/support/tickets/${ticketId}/status`, {
      status,
      priority,
    });
    return res.data;
  },

  adminGetStats: async () => {
    const res = await api.get("/admin/support/stats");
    return res.data;
  },

  adminGetUnreadCount: async () => {
    const res = await api.get("/admin/support/unread-count");
    return res.data;
  },
};

export default supportService;